import { action, computed, observable } from 'mobx';
import { computedFn } from 'mobx-utils';
import TranslationRepository from '../../repositories/TranslationRepository';
import { RESTRICTION_ERRORS } from '../../constants/Errors';
import Config, { CONVENIENCE_STORE, IAddressDisplaySchema } from '../../domain/entities/Config/Config';
import Address, { IAddressAttributes } from '../../domain/entities/Address';
import AddressFormStore from '../../domain/entities/AddressFormStore';
import Api, { IApiErrorElement } from '../../api';
import { IAddressValidateResponse, IMunicipalityColonies, IUpsIndicator, IGenericAddressOptionsResponse } from '../../api/IApiResponses';
import { ADDRESS_TYPE, ADDRESS_TYPE_ID, FIELD_TYPE } from '../../constants/Constants';
import NotificationsCollection from '../../domain/entities/Notification/NotificationsCollection';
import normalizeApiErrors, { normalizeErrorElements } from '../../helpers/normalizeApiErrors';
import { IAddressSchema, IAddressSchemaProperties, IJsonSchemaAddressType } from '../../api/JsonSchemaApi';
import { getCountryAbbr } from '../../utils/AddressFormUtils';
import { translationKeysFieldMappings } from '../../constants/AddressFieldsStaticDefinitions';
import { IDeliveryMethodsMapping } from '../../api/IDataMappings';
import { getAddressDisplaySchema } from '../../utils/AddressUtils';
import MelissaApi from '../../api/MelissaApi';
import { MELISSA_MAPPINGS } from '../../api/dataMapper/melissaApiMapper';
import { NOTIFICATION_GROUP } from '../../constants/Notifications';
import EnhanceNotificationsCollectionUseCase from '../../use-cases/EnhanceNotificationsCollectionUseCase';

export interface IAddressRepositoryConfig {
  addressFormStore: AddressFormStore;
  api: Api;
  notificationsCollection: NotificationsCollection;
  config: Config;
  melissaApi?: MelissaApi;
  translationRepository: TranslationRepository;
}

export interface ILoadAddressSchemaOptions {
  convenienceStoreType?: CONVENIENCE_STORE;
}

class AddressRepository {
  @observable public isUpdating = false;

  @observable public isSavingNew = false;

  @observable protected _addresses: Address [] = [];

  @observable public readonly formStore: AddressFormStore;

  @observable notificationsCollection: NotificationsCollection;

  @observable config: Config;

  @observable addressType: IJsonSchemaAddressType;

  @observable public addressListSource: string;

  @observable public addressSchemas: {
    [key: string]: IAddressSchema;
  } = {};

  @observable private pendingAddressSchemas: {
    [key: string]: Promise<IAddressSchema>;
  } = {};

  public api: Api;

  private melissaApi: MelissaApi;

  private translationRepository: TranslationRepository;

  private cachedAddressLookupData = new Map<string, IGenericAddressOptionsResponse>();

  constructor(props: IAddressRepositoryConfig) {
    this.api = props.api;
    this.melissaApi = props.melissaApi;
    this.notificationsCollection = props.notificationsCollection;
    this.config = props.config;
    this.translationRepository = props.translationRepository;
    this.formStore = props.addressFormStore;
  }

  @computed
  public get addresses(): Address[] {
    return this._addresses.slice();
  }

  @computed
  public get activeSchema(): IAddressSchema {
    return this.formStore?.addressSchema;
  }

  @computed
  private get addressInputSchema(): IAddressSchemaProperties {
    return this.addressType === ADDRESS_TYPE.BILLING
      ? this.config.billingAddressInputSchema
      : this.config.shippingAddressInputSchema;
  }

  @computed
  get shouldEnableNewSchema(): boolean {
    return (
      this.config?.jsonSchemasEnabled && (
        this.addressType === ADDRESS_TYPE.BILLING
        || this.addressType === ADDRESS_TYPE.SHIPPING
      )
    );
  }

  @computed
  public get defaultShippingAddress(): Address {
    const deliveryAddresses = this._addresses.filter(
      address => {
        const addressType = (
          address.attributes[FIELD_TYPE.ADDRESS_TYPE] || ADDRESS_TYPE_ID.SHIPPING
        ) as ADDRESS_TYPE_ID;

        return [ADDRESS_TYPE_ID.SHIPPING, ADDRESS_TYPE_ID.BILLING].includes(addressType);
      },
    );

    return deliveryAddresses?.find(address => address.isDefault)
      || deliveryAddresses?.[0];
  }

  @action
  setAddresses = (addresses: IAddressAttributes[]): void => {
    this._addresses = addresses.map(addressData => Address.fromJSON(addressData));
  };

  @action
  async updateAddressSchemaByCountry(countryId: number): Promise<void> {
    let schema: IAddressSchema = this.formStore.addressSchema;
    if (this.shouldEnableNewSchema) {
      schema = await this.loadAddressSchema(countryId);
    }

    const hasStates = !!this.config.getStates(countryId)?.length;

    this.formStore.setAddressSchema(schema, {
      hasStates,
    });
  }

  @action
  public updateAddressValues = (address: Address): Address | null => {
    const existingAddressIndex = this._addresses.findIndex(addr => (
      Number(addr.id) === Number(address.id)));
    if (existingAddressIndex >= 0) {
      this._addresses.splice(existingAddressIndex, 1, address);

      return address;
    }

    return null;
  };

  @action
  public addAddress = (address: Address): void => {
    const existingAddressIndex = this._addresses.find(addr => (
      Number(addr.id) === Number(address.id)));
    if (!existingAddressIndex) {
      this._addresses.push(address);
    }
  };

  public getAddressById = computedFn(function getAddressById(id): Address {
    return this._addresses.find((address: Address) => address.id === id);
  });

  public async getAddressSuggestions(address: Address): Promise<{ suggestions: Address[], indicators: IUpsIndicator[] }> {
    try {
      const response = await this.api.getAddressSuggestions(Address.toJSON(address));
      return {
        indicators: response.indicators ?? [],
        suggestions: response.suggestions.map(suggestion => Address.fromJSON(suggestion as IAddressAttributes)),
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return { indicators: [], suggestions: [] };
    }
  }

  public async validateAddress(address: Address, validateType: string): Promise<IAddressValidateResponse> {
    try {
      const addressData = Address.toJSON(address);
      const response: IAddressValidateResponse = await this.api.validateAddress(addressData, validateType);

      if (response.errors) {
        this.setHazmatShippingError(response.errors);
      }

      return response;
    } catch (error) {
      this.handleAddressError(error, NOTIFICATION_GROUP.SHIPPING);
    }
  }

  protected setHazmatShippingError = (errors: IApiErrorElement[]): void => {
    const normalizedErrors = normalizeErrorElements(
      errors,
      NOTIFICATION_GROUP.SHIPPING,
    );
    this.notificationsCollection.addNotifications(normalizedErrors);

    const hasHazmatError = this.notificationsCollection.hasNotification(
      RESTRICTION_ERRORS.RESTRICTION_APO_ADDRESS, NOTIFICATION_GROUP.SHIPPING,
    ) || this.notificationsCollection.hasNotification(
      RESTRICTION_ERRORS.RESTRICTION_APO_ADDRESS_MILITARY, NOTIFICATION_GROUP.SHIPPING,
    ) || this.notificationsCollection.hasNotification(
      RESTRICTION_ERRORS.RESTRICTION_APO_ADDRESS_TERRITORY, NOTIFICATION_GROUP.SHIPPING,
    ) || this.notificationsCollection.hasNotification(
      RESTRICTION_ERRORS.RESTRICTION_APO_ADDRESS_POBOX, NOTIFICATION_GROUP.SHIPPING,
    );

    if (hasHazmatError) {
      throw new Error(RESTRICTION_ERRORS.RESTRICTION_APO_ADDRESS);
    }
  };

  public getMunicipalityandColoniesByZip = async (
    zipcode: string,
  ): Promise<IMunicipalityColonies> => {
    try {
      const resp = await this.api.getMunicipalityandColoniesByZip(zipcode);
      if (resp) {
        return resp as IMunicipalityColonies;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
    return { colonies: [], municipality: '' };
  };

  @computed
  public get newAddressTemplate(): Address {
    return new Address();
  }

  @action
  protected setIsSavingNew(isSavingNew: boolean): void {
    this.isSavingNew = isSavingNew;
  }

  @action
  protected setIsUpdating(isUpdating: boolean): void {
    this.isUpdating = isUpdating;
  }

  getAddressFromZip = async (
    { zipcode, zipcodeApiPath, fields }: { zipcode: string, zipcodeApiPath?: string, fields?: FIELD_TYPE[] },
  ): Promise<Address[]> => {
    let addressSuggestions: IAddressAttributes[];
    try {
      if (this.config.supportsTypeaheadInput) {
        const fieldsString = fields.map(fieldType => MELISSA_MAPPINGS[fieldType]).join(' ');
        const countryAbbr = getCountryAbbr(this.config.countryId);
        const response = await this.melissaApi.getAddressSuggestionsFromPostalCode(zipcode, fieldsString, countryAbbr);
        addressSuggestions = response;
      } else {
        const response = await this.api.getSuggestionAddressFromZip(zipcode, zipcodeApiPath);
        addressSuggestions = response.suggestions;
      }
      return addressSuggestions.map(address => Address.fromJSON(address as IAddressAttributes));
    } catch (e) {
      console.error(e);
      return [];
    }
  };

  clearAutoFillFields = (autoFillFields: Array<keyof IAddressAttributes>): void => {
    autoFillFields.map(val => this.formStore.setFieldValue(val, ''));
  };

  getGenericAddressOptions = async (path: string): Promise<IGenericAddressOptionsResponse> => {
    const lookupData = this.cachedAddressLookupData.get(path);

    if (lookupData) {
      return lookupData;
    } else {
      const response = await this.api.getAddressOptionsFromRemote(path);
      this.cachedAddressLookupData.set(path, response);

      return response || {};
    }
  };

  @action
  protected fetchAndSetAddressSchema = async (
    countryId: number,
    options?: ILoadAddressSchemaOptions,
  ): Promise<IAddressSchema> => {
    const { brandId, brandName, regionId, locale } = this.config;
    const { convenienceStoreType } = options || {};
    const cvsKey =  (!!convenienceStoreType) ? `-cvs-${convenienceStoreType}` : '';
    const schemaId = `${brandId}-${regionId}-${locale}-${countryId}-${this.addressType}${cvsKey}`;

    if (!this.addressSchemas[schemaId]) {
      if (!this.pendingAddressSchemas[schemaId]) {
        this.pendingAddressSchemas[schemaId] = this.api.getAddressJsonSchema({
          addressType: this.addressType,
          brandName: `${brandName}`,
          convenienceStoreType,
          countryId,
          locale: `${locale}`,
        });
      }
      try {
        this.addressSchemas[schemaId] = await this.pendingAddressSchemas[schemaId];
      } finally {
        delete this.pendingAddressSchemas[schemaId];
      }
    }

    const addressSchema = this.addressSchemas[schemaId];
    const selectedCountryId = this.formStore.values[FIELD_TYPE.COUNTRY_ID];
    if (!selectedCountryId || selectedCountryId === countryId) {
      this.formStore.setAddressSchema(addressSchema);
    }

    return addressSchema;
  };

  @action
  public loadAddressSchema = async (
    countryId: number,
    options?: ILoadAddressSchemaOptions,
  ): Promise<IAddressSchema> => this.fetchAndSetAddressSchema(countryId, options);

  public getAddressDisplaySchema = async (
    countryId: number,
  ): Promise<IAddressDisplaySchema> => {

    if (this.config.jsonSchemasEnabled) {
      const addressSchema = await this.loadAddressSchema(countryId);
      return getAddressDisplaySchema(addressSchema?.properties);
    } else {
      return getAddressDisplaySchema(this.addressInputSchema);
    }
  };

  @action
  public setAddressListSource = (source: string): void => {
    this.addressListSource = source;
  };

  private getSchemaInvalidTranslationKeys(
    properties: IAddressSchemaProperties = {},
  ): string[] {
    return Object.values(properties)
      .map(property => property?.additionalProperties?.invalidKey)
      .filter((key): key is string => !!key);
  }

  async loadInputsTranslations(): Promise<void> {
    const defaultTranslationKeys = this.formStore.fieldTypes.reduce((acc, item) => {
      const values = Object.values(translationKeysFieldMappings[item] || {});
      return [...acc, ...values];
    }, []);
    const schemaInvalidTranslationKeys = this.getSchemaInvalidTranslationKeys(this.formStore.addressSchema?.properties);
    const translationKeys = [...new Set([...defaultTranslationKeys, ...schemaInvalidTranslationKeys])];
    const translationCollection = this.translationRepository?.listRequiredTranslationSchema(translationKeys);
    this.formStore.setTranslations(translationCollection);
  }

  getSuggestionFromAddressSearch = async (
    keywords: string,
    fields: FIELD_TYPE[],
  ): Promise<Address[]> => {
    const fieldsString = fields.map(fieldType => MELISSA_MAPPINGS[fieldType]).join(' ');
    const results = await this.melissaApi.getAddressSuggestionsFromPartialAddress(keywords, fieldsString);
    return results.map(address => Address.fromJSON(address as IAddressAttributes));
  };
  
  getAddressFieldsFromSelectedSuggestionValueLegacy = async (
    keywords: string,
    addressValue: string,
    countryId: number,
  ): Promise<Partial<IAddressAttributes>> => {
    return keywords && this.api.getAddressFieldsFromSelectedSuggestionValueLegacy(keywords, addressValue, countryId);
  };

  getSuggestionFromAddressSearchLegacy = async (
    keywords: string, countryId: number,
  ): Promise<{ label: string, value: string }[]> => {
    try {
      return await this.api.getSuggestionFromAddressSearchLegacy(keywords, countryId);
    } catch (e) {
      // silently handle search failures
      return [];
    }
  };

  getDeliveryMethodsFromZip = async (zipcode: string): Promise<IDeliveryMethodsMapping> => {
    try {
      return await this.api.getDeliveryMethodsFromZip(zipcode);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return { deliveryMethods: [], orderSummary: null };
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected handleAddressError = (error: any, notificationGroup: NOTIFICATION_GROUP, signal?: AbortSignal): void => {
    if (!signal?.aborted) {
      const normalizedErrors = normalizeApiErrors(error, notificationGroup);
      this.notificationsCollection.addNotifications(normalizedErrors);
      const enhanceNotificationsCollectionUseCase = new EnhanceNotificationsCollectionUseCase();
      enhanceNotificationsCollectionUseCase.execute();
    }
    throw error;
  };
}

export default AddressRepository;
